import React, { useState } from 'react';
import { signIn as amplifySignIn, signUp as amplifySignUp, confirmSignUp, resetPassword, confirmResetPassword, signInWithRedirect, signOut as amplifySignOut } from 'aws-amplify/auth';
import { useAuth } from '../../context/AuthContext';
import './Login.css';

const VIEWS = {
  SIGN_IN: 'signIn',
  SIGN_UP: 'signUp',
  CONFIRM_SIGN_UP: 'confirmSignUp',
  FORGOT_PASSWORD: 'forgotPassword',
  CONFIRM_RESET: 'confirmReset',
};

export default function Login() {
  const { signIn: syncLocalAuth } = useAuth();
  const [view, setView] = useState(VIEWS.SIGN_IN);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const switchView = (v) => { setView(v); setError(''); setMessage(''); };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      // Clear any stale cached session before attempting sign-in
      try { await amplifySignOut(); } catch (_) { /* no session to clear */ }
      const result = await amplifySignIn({ username: email, password });
      if (result.isSignedIn) {
        await syncLocalAuth();
      } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
        setMessage('Please verify your email first.');
        setView(VIEWS.CONFIRM_SIGN_UP);
      } else {
        setError('Additional step required: ' + (result.nextStep?.signInStep || 'unknown'));
      }
    } catch (err) {
      if (err.name === 'UserAlreadyAuthenticatedException') {
        try {
          await amplifySignOut();
          const result = await amplifySignIn({ username: email, password });
          if (result.isSignedIn) { await syncLocalAuth(); setLoading(false); return; }
        } catch (retryErr) {
          setError(retryErr.message || 'Sign in failed after clearing session');
          setLoading(false);
          return;
        }
      }
      setError(err.message || 'Sign in failed');
    }
    setLoading(false);
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError(''); setMessage(''); setLoading(true);
    try {
      const result = await amplifySignUp({
        username: email,
        password,
        options: { userAttributes: { email, name } },
      });
      if (result.isSignUpComplete) {
        setMessage('Account created! Signing you in...');
        try {
          try { await amplifySignOut(); } catch (_) { /* ignore */ }
          const signInResult = await amplifySignIn({ username: email, password });
          if (signInResult.isSignedIn) await syncLocalAuth();
        } catch { switchView(VIEWS.SIGN_IN); setMessage('Account created! Please sign in.'); }
      } else {
        setMessage('Check your email for a verification code.');
        switchView(VIEWS.CONFIRM_SIGN_UP);
      }
    } catch (err) {
      setError(err.message || 'Sign up failed');
    }
    setLoading(false);
  };

  const handleConfirmSignUp = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const result = await confirmSignUp({ username: email, confirmationCode: code });
      if (result.isSignUpComplete) {
        setMessage('Verified! Signing you in...');
        try {
          try { await amplifySignOut(); } catch (_) { /* ignore */ }
          const signInResult = await amplifySignIn({ username: email, password });
          if (signInResult.isSignedIn) await syncLocalAuth();
        } catch { switchView(VIEWS.SIGN_IN); setMessage('Verified! Please sign in.'); }
      }
    } catch (err) {
      setError(err.message || 'Confirmation failed');
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError(''); setMessage(''); setLoading(true);
    try {
      await resetPassword({ username: email });
      setMessage('Check your email for a reset code.');
      switchView(VIEWS.CONFIRM_RESET);
    } catch (err) {
      setError(err.message || 'Failed to send reset code');
    }
    setLoading(false);
  };

  const handleConfirmReset = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
      setMessage('Password reset! Signing you in...');
      try {
        try { await amplifySignOut(); } catch (_) { /* ignore */ }
        const result = await amplifySignIn({ username: email, password: newPassword });
        if (result.isSignedIn) await syncLocalAuth();
      } catch { switchView(VIEWS.SIGN_IN); setMessage('Password reset! Please sign in.'); }
    } catch (err) {
      setError(err.message || 'Reset failed');
    }
    setLoading(false);
  };

  const handleGoogleSignIn = async () => {
    try { await amplifySignOut(); } catch (_) { /* no session to clear */ }
    signInWithRedirect({ provider: 'Google' });
  };

  return (
    <div className="login-page">
      <div className="login-bg">
        <div className="login-bg-ball"></div>
        <div className="login-bg-ball login-bg-ball--2"></div>
        <div className="login-bg-ball login-bg-ball--3"></div>
      </div>
      <div className="login-card">
        <div className="login-logo">
          <span className="login-logo-icon">⚽</span>
          <h1>Vi Fyker</h1>
          <p className="login-subtitle">
            {view === VIEWS.SIGN_IN && 'Sign in to continue'}
            {view === VIEWS.SIGN_UP && 'Create your account'}
            {view === VIEWS.CONFIRM_SIGN_UP && 'Verify your email'}
            {view === VIEWS.FORGOT_PASSWORD && 'Reset your password'}
            {view === VIEWS.CONFIRM_RESET && 'Enter reset code'}
          </p>
        </div>

        {(view === VIEWS.SIGN_IN || view === VIEWS.SIGN_UP) && (
          <>
            <button className="login-google-btn" onClick={handleGoogleSignIn} type="button">
              <svg width="18" height="18" viewBox="0 0 18 18" style={{ marginRight: 8 }}>
                <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
                <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"/>
                <path fill="#FBBC05" d="M3.964 10.706A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"/>
                <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58z"/>
              </svg>
              Continue with Google
            </button>
            <div className="login-divider"><span>or</span></div>
          </>
        )}

        {error && <div className="login-error">{error}</div>}
        {message && <div className="login-message">{message}</div>}

        {view === VIEWS.SIGN_IN && (
          <form onSubmit={handleSignIn} className="login-form">
            <div className="login-field">
              <label htmlFor="email">Email</label>
              <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email" autoComplete="email" required />
            </div>
            <div className="login-field">
              <label htmlFor="password">Password</label>
              <input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" autoComplete="current-password" required />
            </div>
            <button type="submit" className="login-btn" disabled={loading}>{loading ? 'Signing in...' : 'Sign In'}</button>
            <div className="login-links">
              <button type="button" className="login-link" onClick={() => switchView(VIEWS.FORGOT_PASSWORD)}>Forgot password?</button>
              <button type="button" className="login-link" onClick={() => switchView(VIEWS.SIGN_UP)}>Create account</button>
            </div>
          </form>
        )}

        {view === VIEWS.SIGN_UP && (
          <form onSubmit={handleSignUp} className="login-form">
            <div className="login-field">
              <label htmlFor="name">Name</label>
              <input id="name" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" autoComplete="name" required />
            </div>
            <div className="login-field">
              <label htmlFor="signup-email">Email</label>
              <input id="signup-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email" autoComplete="email" required />
            </div>
            <div className="login-field">
              <label htmlFor="signup-password">Password</label>
              <input id="signup-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Choose a password" autoComplete="new-password" required />
            </div>
            <button type="submit" className="login-btn" disabled={loading}>{loading ? 'Creating account...' : 'Create Account'}</button>
            <div className="login-links">
              <button type="button" className="login-link" onClick={() => switchView(VIEWS.SIGN_IN)}>Already have an account? Sign in</button>
            </div>
          </form>
        )}

        {view === VIEWS.CONFIRM_SIGN_UP && (
          <form onSubmit={handleConfirmSignUp} className="login-form">
            <div className="login-field">
              <label htmlFor="code">Verification Code</label>
              <input id="code" type="text" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Enter the code from your email" autoComplete="one-time-code" required />
            </div>
            <button type="submit" className="login-btn" disabled={loading}>{loading ? 'Verifying...' : 'Verify'}</button>
            <div className="login-links">
              <button type="button" className="login-link" onClick={() => switchView(VIEWS.SIGN_IN)}>Back to sign in</button>
            </div>
          </form>
        )}

        {view === VIEWS.FORGOT_PASSWORD && (
          <form onSubmit={handleForgotPassword} className="login-form">
            <div className="login-field">
              <label htmlFor="reset-email">Email</label>
              <input id="reset-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email" autoComplete="email" required />
            </div>
            <button type="submit" className="login-btn" disabled={loading}>{loading ? 'Sending...' : 'Send Reset Code'}</button>
            <div className="login-links">
              <button type="button" className="login-link" onClick={() => switchView(VIEWS.SIGN_IN)}>Back to sign in</button>
            </div>
          </form>
        )}

        {view === VIEWS.CONFIRM_RESET && (
          <form onSubmit={handleConfirmReset} className="login-form">
            <div className="login-field">
              <label htmlFor="reset-code">Reset Code</label>
              <input id="reset-code" type="text" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Enter the code from your email" autoComplete="one-time-code" required />
            </div>
            <div className="login-field">
              <label htmlFor="new-password">New Password</label>
              <input id="new-password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Choose a new password" autoComplete="new-password" required />
            </div>
            <button type="submit" className="login-btn" disabled={loading}>{loading ? 'Resetting...' : 'Reset Password'}</button>
            <div className="login-links">
              <button type="button" className="login-link" onClick={() => switchView(VIEWS.SIGN_IN)}>Back to sign in</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
